import { useState } from "react";

interface BookCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const INDUSTRIES = [
  "Home Services",
  "Legal",
  "Medical / Medspa",
  "Financial Services",
  "Other",
];

const BookCallModal = ({ isOpen, onClose }: BookCallModalProps) => {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [msgLen, setMsgLen] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value as string);
    });

    if (msgLen > 500) return;

    setStatus("submitting");
    try {
      const res = await fetch("https://readdy.ai/api/form/d71bai9lum2co2q1fk7g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl w-full max-w-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-sapphire px-8 py-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Book a Strategy Call</h2>
              <p className="text-white/70 text-sm mt-1">Tell us about your business. We'll build your system.</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer mt-1"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {status === "success" ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-500"></i>
              </div>
              <h3 className="text-lg font-bold text-charcoal mb-2">You're all set!</h3>
              <p className="text-sm text-gray-500 mb-6">
                We'll reach out within 1 business day to confirm your strategy call.
              </p>
              <button
                onClick={() => { setStatus("idle"); onClose(); }}
                className="bg-sapphire hover:bg-deep-sapphire text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="book-strategy-call-form"
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-sapphire transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-sapphire transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 (555) 000-0000"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-sapphire transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Company Name</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Your Business"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-sapphire transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-charcoal mb-1.5">Industry</label>
                <select
                  name="industry"
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-sapphire transition-colors cursor-pointer"
                >
                  <option value="">Select your industry</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-charcoal mb-1.5">
                  Tell us about your business
                  <span className={`ml-2 font-normal ${msgLen > 500 ? "text-red-500" : "text-gray-400"}`}>
                    ({msgLen}/500)
                  </span>
                </label>
                <textarea
                  name="message"
                  rows={3}
                  placeholder="Describe your biggest operational challenge..."
                  maxLength={500}
                  onChange={(e) => setMsgLen(e.target.value.length)}
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-sapphire transition-colors resize-none"
                />
              </div>

              {status === "error" && (
                <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={status === "submitting" || msgLen > 500}
                className="w-full bg-sapphire hover:bg-deep-sapphire disabled:opacity-60 text-white text-sm font-semibold py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {status === "submitting" ? "Sending..." : "Book My Strategy Call"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookCallModal;
